import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  addCreditReason,
  creditNotesQuery,
  creditReasonsQuery,
  creditState,
  REASON_KINDS,
  updateCreditReason,
  type CreditNoteSummary,
  type CreditReason,
  type CreditReasonKind,
} from '../api/credit-notes.ts';
import { meQuery } from '../api/me.ts';
import { dayLabel } from '../api/quotes.ts';

import { EditButton, IconButton } from '../components/action-buttons.tsx';
import { icons } from '../components/icons.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { formatMinorUnits } from '../money.ts';

// CB-REQ FIN-02 at v1.993: every credit, under Invoices, and the tenant's own wording for why it
// credits. A credit is started from its invoice, so there is no "New credit" here.

export function CreditsScreen() {
  const credits = useQuery(creditNotesQuery());
  const drafts = credits.data?.filter((c) => c.status === 'draft').length ?? 0;
  return (
    <div className="flex flex-col gap-6">
      <Link to="/invoices" className="link self-start text-sm">
        ← Invoices
      </Link>
      <PageHeader
        kicker="Finance"
        title="Credits"
        meta={
          credits.data
            ? `${credits.data.length} ${credits.data.length === 1 ? 'credit' : 'credits'}${
                drafts > 0 ? ` · ${drafts} to issue` : ''
              }`
            : undefined
        }
        actions={
          <Link to="/credit-note-reasons" className="link text-sm">
            Reasons
          </Link>
        }
      />
      {credits.isPending ? <p className="text-muted">Loading…</p> : null}
      {credits.isError ? (
        <p role="alert" className="alert">
          {credits.error.message}
        </p>
      ) : null}
      {credits.data?.length === 0 ? (
        <p className="text-ink-soft">No credits yet. Credit an issued invoice from its page.</p>
      ) : null}
      {credits.data && credits.data.length > 0 ? <CreditTable rows={credits.data} /> : null}
    </div>
  );
}

export function CreditTable({ rows }: { rows: CreditNoteSummary[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <caption className="sr-only">Credits: drafts first, then the newest issued</caption>
        <thead>
          <tr>
            <th scope="col">Credit</th>
            <th scope="col">Invoice</th>
            <th scope="col">Client</th>
            <th scope="col">Issued</th>
            <th scope="col" className="text-right">
              Total
            </th>
            <th scope="col">State</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const state = creditState(c);
            return (
              <tr key={c.id}>
                <th scope="row" className="font-semibold">
                  <Link
                    to="/credit-notes/$creditNoteId"
                    params={{ creditNoteId: c.id }}
                    className="link"
                  >
                    {c.reference ?? 'Draft'}
                  </Link>
                </th>
                <td>
                  <Link
                    to="/invoices/$invoiceId"
                    params={{ invoiceId: c.invoice.id }}
                    className="link"
                  >
                    {c.invoice.reference}
                  </Link>
                </td>
                <td>{c.clientName}</td>
                <td className="text-sm">{c.issueDate ? dayLabel(c.issueDate) : '—'}</td>
                <td className="text-right tabular-nums">
                  {formatMinorUnits(c.total.amountMinor, c.total.currency)}
                </td>
                <td>
                  <span
                    className={
                      state === 'Draft' || state === 'Credit held' ? 'tag tag-accent' : 'tag'
                    }
                  >
                    {state}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function CreditReasonsScreen() {
  const reasons = useQuery(creditReasonsQuery);
  const me = useQuery(meQuery);
  const queryClient = useQueryClient();
  const mayManage = me.data?.capabilities.includes('invoices.manage') ?? false;
  const refresh = () => queryClient.invalidateQueries({ queryKey: creditReasonsQuery.queryKey });
  const add = useMutation({
    mutationFn: ({ label, kind }: { label: string; kind: CreditReasonKind }) =>
      addCreditReason(label, kind),
    onSuccess: refresh,
  });
  const update = useMutation({
    mutationFn: ({ id, change }: { id: string; change: { label?: string; retired?: true } }) =>
      updateCreditReason(id, change),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-6">
      <Link to="/credit-notes" className="link self-start text-sm">
        ← Credits
      </Link>
      <PageHeader kicker="Credits" title="Reasons" />
      {reasons.isError ? (
        <p role="alert" className="alert">
          {reasons.error.message}
        </p>
      ) : null}
      {reasons.data ? (
        <ReasonsTable
          reasons={reasons.data}
          mayManage={mayManage}
          error={update.error}
          onRename={(id, label) => update.mutate({ id, change: { label } })}
          onRetire={(id) => update.mutate({ id, change: { retired: true } })}
        />
      ) : null}
      {mayManage ? (
        <ReasonForm
          pending={add.isPending}
          error={add.error}
          onSubmit={(label, kind) => add.mutate({ label, kind })}
        />
      ) : null}
    </div>
  );
}

const kindLabel = (kind: CreditReasonKind) => REASON_KINDS.find(([k]) => k === kind)?.[1] ?? kind;

export function ReasonsTable({
  reasons,
  mayManage,
  error,
  onRename,
  onRetire,
}: {
  reasons: CreditReason[];
  mayManage: boolean;
  error: Error | null;
  onRename: (id: string, label: string) => void;
  onRetire: (id: string) => void;
}) {
  const [editing, setEditing] = useState<{ id: string; label: string } | null>(null);
  return (
    <div className="flex flex-col gap-2">
      <table className="data-table">
        <caption className="sr-only">The reasons offered when crediting, and their kind</caption>
        <thead>
          <tr>
            <th scope="col">Reason</th>
            <th scope="col">Kind</th>
            <th scope="col">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {reasons.map((r) => (
            <tr key={r.id} className={r.retired ? 'text-ink-soft' : ''}>
              <th scope="row" className="font-normal">
                {editing?.id === r.id ? (
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (editing.label.trim()) onRename(r.id, editing.label.trim());
                      setEditing(null);
                    }}
                  >
                    <input
                      className="input"
                      aria-label={`New wording for ${r.label}`}
                      value={editing.label}
                      onChange={(e) => setEditing({ id: r.id, label: e.currentTarget.value })}
                    />
                    <Button type="submit" variant="quiet">
                      Save
                    </Button>
                  </form>
                ) : (
                  <>
                    {r.label}
                    {r.retired ? ' (retired)' : ''}
                  </>
                )}
              </th>
              <td>{kindLabel(r.kind)}</td>
              <td className="text-right">
                {mayManage && !r.retired && editing?.id !== r.id ? (
                  <span className="inline-flex gap-1">
                    <EditButton
                      what={r.label}
                      onClick={() => setEditing({ id: r.id, label: r.label })}
                    />
                    <IconButton
                      icon={icons.retire}
                      label={`Retire ${r.label}`}
                      onClick={() => onRetire(r.id)}
                    />
                  </span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

function ReasonForm({
  pending,
  error,
  onSubmit,
}: {
  pending: boolean;
  error: Error | null;
  onSubmit: (label: string, kind: CreditReasonKind) => void;
}) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<CreditReasonKind>('error');
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!label.trim()) return;
    onSubmit(label.trim(), kind);
    setLabel('');
  }
  return (
    <form
      onSubmit={submit}
      aria-labelledby="new-reason"
      className="flex flex-col gap-4 border-2 border-divider p-4"
    >
      <h2 id="new-reason" className="section-title">
        + Reason
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          label="Wording"
          required
          value={label}
          onChange={(e) => setLabel(e.currentTarget.value)}
        />
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Kind</span>
          <select
            className="input"
            value={kind}
            onChange={(e) => setKind(e.currentTarget.value as CreditReasonKind)}
          >
            {REASON_KINDS.map(([value, name]) => (
              <option key={value} value={value}>
                {name}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          Add
        </Button>
      </div>
    </form>
  );
}
